import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePageMetadata } from '../hooks/usePageMetadata';
import '../styles/quiz.css';
import { ChevronRight, TrendingUp, Clock, DollarSign, Award, AlertCircle, CheckCircle, ArrowRight, Zap, Shield, Users, BarChart3, Sparkles, Download, Brain } from 'lucide-react';
import { LeadFormModal } from '../components/LeadFormModal';
import { ParticlesBackground } from '../components/effects/ParticlesBackground';
import { AnimatedBlob } from '../components/effects/AnimatedBlob';
import { RevealOnScroll } from '../components/effects/RevealOnScroll';
import { GlassCard } from '../components/ui/GlassCard';
import { StatCard } from '../components/ui/StatCard';

export const AiQuizPage: React.FC = () => {
    usePageMetadata({
        title: "Quiz AI | Sprawdź Potencjał Automatyzacji w 3 Minuty",
        description: "Odpowiedz na 9 pytań i otrzymaj analizę gotowości Twojej firmy na AI. Odkryj potencjalne oszczędności, rozwiązania i szacowany zwrot z inwestycji.",
    });

    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [answers, setAnswers] = useState<{[key: string]: any}>({});
    const [showResults, setShowResults] = useState(false);
    const [industry, setIndustry] = useState('');
    const [teamSize, setTeamSize] = useState(1);
    const [userSegment, setUserSegment] = useState<any>(null);
    const [hoveredOption, setHoveredOption] = useState<number | null>(null);

    const questionCategories = {
        pain_points: { weight: 0.35, label: "Identyfikacja Wyzwań" },
        readiness_assessment: { weight: 0.25, label: "Dojrzałość Cyfrowa" },
        budget_discovery: { weight: 0.20, label: "Potencjał Inwestycyjny" },
        urgency_triggers: { weight: 0.20, label: "Pilność Wdrożenia" }
    };

    const industryMapping: {[key: string]: any} = {
        'e-commerce': { painPoint: 'obsługę zapytań o status zamówienia', multiplier: 1.4, icon: '🛍️' },
        'uslugi': { painPoint: 'planowanie wizyt i przypomnienia', multiplier: 1.2, icon: '💼' },
        'produkcja': { painPoint: 'raportowanie stanu magazynu', multiplier: 1.5, icon: '🏭' },
        'it': { painPoint: 'odpowiadanie na powtarzalne tickety', multiplier: 1.3, icon: '💻' },
        'finanse': { painPoint: 'analizę dokumentów i weryfikację', multiplier: 1.6, icon: '💰' },
        'zdrowie': { painPoint: 'zarządzanie dokumentacją pacjentów', multiplier: 1.7, icon: '🏥' }
    };

    const questions = [
        {
            id: 'industry', category: 'readiness_assessment',
            question: 'W jakiej branży działa Twoja firma?', type: 'single',
            options: [
                { text: '🛍️ E-commerce / Retail', value: 'e-commerce', points: 10 },
                { text: '💼 Usługi B2B', value: 'uslugi', points: 10 },
                { text: '🏭 Produkcja', value: 'produkcja', points: 10 },
                { text: '💻 IT / Software', value: 'it', points: 10 },
                { text: '💰 Finanse / Banking', value: 'finanse', points: 10 },
                { text: '🏥 Zdrowie / Medycyna', value: 'zdrowie', points: 10 }
            ]
        },
        {
            id: 'team_size', category: 'readiness_assessment',
            question: 'Jak duży jest Twój zespół?', type: 'single',
            options: [
                { text: '1-5 osób', value: 5, points: 15, insight: 'startup_agility' },
                { text: '6-20 osób', value: 20, points: 25, insight: 'growth_phase' },
                { text: '21-50 osób', value: 50, points: 35, insight: 'scale_ready' },
                { text: '51-200 osób', value: 200, points: 45, insight: 'enterprise_potential' },
                { text: '200+ osób', value: 500, points: 55, insight: 'corporate_transformation' }
            ]
        },
        {
            id: 'time_waste', category: 'pain_points',
            question: 'Ile czasu dziennie Twój zespół traci na ${painPoint}?', type: 'single', isDynamic: true,
            options: [
                { text: '< 30 minut', value: 0.5, points: 10, insight: 'low_automation_potential' },
                { text: '30 min - 2 godziny', value: 2, points: 25, insight: 'moderate_pain_point' },
                { text: '2-5 godzin', value: 5, points: 40, insight: 'high_urgency' },
                { text: '> 5 godzin', value: 8, points: 60, insight: 'critical_bottleneck' },
                { text: 'Nie wiem dokładnie', value: 3, points: 30, insight: 'needs_audit' }
            ]
        },
        {
            id: 'competition', category: 'urgency_triggers',
            question: 'Czy Twoja konkurencja już wykorzystuje AI?', type: 'single',
            options: [
                { text: 'Tak, intensywnie', value: 100, points: 50, insight: 'urgent_catchup' },
                { text: 'Niektórzy zaczynają', value: 75, points: 35, insight: 'competitive_pressure' },
                { text: 'Raczej nie', value: 25, points: 20, insight: 'first_mover_advantage' },
                { text: 'Nie wiem', value: 50, points: 30, insight: 'market_research_needed' }
            ]
        },
        {
            id: 'current_automation', category: 'readiness_assessment',
            question: 'Które procesy już automatyzujesz?', type: 'multiple',
            options: [
                { text: '📧 Email marketing', value: 'email', points: 10 },
                { text: '📊 Raportowanie', value: 'reporting', points: 15 },
                { text: '💬 Obsługa klienta', value: 'support', points: 20 },
                { text: '📈 Analityka', value: 'analytics', points: 15 },
                { text: '🔄 Procesy sprzedażowe', value: 'sales', points: 20 },
                { text: '❌ Żadne z powyższych', value: 'none', points: 5 }
            ]
        },
        {
            id: 'biggest_challenge', category: 'pain_points',
            question: 'Największe wyzwanie Twojej firmy to:', type: 'single',
            options: [
                { text: '🚀 Skalowanie bez zwiększania zespołu', value: 'scaling', points: 45, insight: 'automation_critical' },
                { text: '💰 Redukcja kosztów operacyjnych', value: 'costs', points: 40, insight: 'roi_focused' },
                { text: '😊 Poprawa satysfakcji klientów', value: 'satisfaction', points: 35, insight: 'cx_improvement' },
                { text: '⚡ Przyspieszenie procesów', value: 'speed', points: 40, insight: 'efficiency_boost' },
                { text: '📊 Lepsze wykorzystanie danych', value: 'data', points: 35, insight: 'analytics_potential' }
            ]
        },
        {
            id: 'investment_readiness', category: 'budget_discovery',
            question: 'Jaki poziom inwestycji w AI rozważasz?', type: 'single',
            options: [
                { text: '🌱 Start (< 1000 PLN/mies)', value: 1000, points: 20, insight: 'starter_solutions' },
                { text: '🎯 Growth (1-5k PLN/mies)', value: 5000, points: 35, insight: 'growth_package' },
                { text: '🚀 Scale (5-15k PLN/mies)', value: 15000, points: 50, insight: 'scale_solutions' },
                { text: '💎 Enterprise (15k+ PLN/mies)', value: 30000, points: 65, insight: 'enterprise_transformation' },
                { text: '🤔 Najpierw chcę zobaczyć, czy się opłaca', value: 0, points: 30, insight: 'roi_proof_needed' }
            ]
        },
        {
            id: 'implementation_timeline', category: 'urgency_triggers',
            question: 'Kiedy planujesz wdrożenie AI?', type: 'single',
            options: [
                { text: '🔥 ASAP (w tym miesiącu)', value: 30, points: 60, insight: 'hot_lead' },
                { text: '📆 W ciągu 3 miesięcy', value: 90, points: 45, insight: 'qualified_lead' },
                { text: '🗓️ W tym roku', value: 365, points: 30, insight: 'planning_phase' },
                { text: '🔮 Przyszły rok', value: 500, points: 20, insight: 'early_research' },
                { text: '🤷 Jeszcze nie wiem', value: 0, points: 25, insight: 'education_needed' }
            ]
        },
        {
            id: 'decision_power', category: 'budget_discovery',
            question: 'Jaka jest Twoja rola w procesie decyzyjnym?', type: 'single',
            options: [
                { text: '👑 Podejmuję decyzje (CEO/Owner)', value: 100, points: 50, insight: 'decision_maker' },
                { text: '💼 Rekomenduję rozwiązania (Manager)', value: 75, points: 40, insight: 'influencer' },
                { text: '🔍 Badam opcje (Specialist)', value: 50, points: 30, insight: 'researcher' },
                { text: '📊 Dostarczam dane (Analyst)', value: 25, points: 25, insight: 'data_provider' }
            ]
        },
        {
            id: 'success_metrics', category: 'pain_points',
            question: 'Co będzie miarą sukcesu wdrożenia AI?', type: 'multiple',
            options: [
                { text: '💰 Wzrost przychodów o 20%+', value: 'revenue', points: 20 },
                { text: '⏱️ Oszczędność 10+ godzin/tydzień', value: 'time', points: 20 },
                { text: '😊 NPS > 70', value: 'nps', points: 15 },
                { text: '📉 Redukcja kosztów o 30%+', value: 'costs', points: 20 },
                { text: '🚀 2x szybsza obsługa', value: 'speed', points: 15 }
            ]
        }
    ];

    const calculateResults = () => {
        let totalScore = 0;
        let categoryScores: {[key: string]: number} = {};
        let insights = new Set();

        Object.entries(answers).forEach(([questionId, answer]) => {
            const question = questions.find(q => q.id === questionId);
            if (!question) return;

            let questionScore = 0;

            if (question.type === 'multiple' && Array.isArray(answer)) {
                answer.forEach(optionValue => {
                    const option = question.options.find(o => o.value === optionValue);
                    if (option) {
                        questionScore += option.points;
                        if ('insight' in option && option.insight) insights.add(option.insight);
                    }
                });
            } else {
                const option = question.options.find(o => o.value === answer);
                if (option) {
                    questionScore = option.points;
                    if ('insight' in option && option.insight) insights.add(option.insight);
                }
            }

            const categoryWeight = questionCategories[question.category as keyof typeof questionCategories]?.weight || 1;
            questionScore *= categoryWeight;

            if (!categoryScores[question.category]) categoryScores[question.category] = 0;
            categoryScores[question.category] += questionScore;
            totalScore += questionScore;
        });

        const industryModifier = industryMapping[industry]?.multiplier || 1;
        const sizeModifier = Math.log10(teamSize + 1) * 0.5 + 0.5;
        const finalScore = Math.min(100, totalScore * industryModifier * sizeModifier);

        let segment;
        if (finalScore >= 80) segment = 'GAME_CHANGER';
        else if (finalScore >= 60) segment = 'HIGH_POTENTIAL';
        else if (finalScore >= 40) segment = 'READY_TO_SCALE';
        else if (finalScore >= 20) segment = 'GROWTH_OPPORTUNITY';
        else segment = 'EXPLORATION_PHASE';

        const monthlySavings = Math.round(finalScore * 150 + Math.random() * 3000 + 2000);
        const roiDays = Math.max(14, Math.round(180 - (finalScore * 2)));
        const automationProcesses = Math.ceil(finalScore / 20) + 3;

        return {
            score: Math.round(finalScore), segment,
            insights: Array.from(insights), categoryScores,
            metrics: { monthlySavings, roiDays, automationProcesses, competitiveAdvantage: Math.round(finalScore * 0.8) }
        };
    };

    const getRecommendations = (segment: string) => {
        const recommendations: {[key: string]: any} = {
            GAME_CHANGER: {
                title: 'GAME CHANGER - Krytyczny Moment Transformacji!',
                description: 'Twoja firma traci dziesiątki tysięcy złotych miesięcznie przez brak automatyzacji. Konkurencja już wdraża AI!',
                solutions: [
                    { name: 'Agent AI 24/7', saving: '15,000 PLN/mies', icon: <Zap className="w-5 h-5" /> },
                    { name: 'Automatyzacja LeadGen', result: '5000+ leadów/mies', icon: <TrendingUp className="w-5 h-5" /> },
                    { name: 'Predykcyjna Analityka', result: '3x zwrot', icon: <BarChart3 className="w-5 h-5" /> },
                    { name: 'AI Success Manager', result: 'Dedykowane wsparcie', icon: <Shield className="w-5 h-5" /> }
                ],
                urgency: 'Każdy dzień zwłoki = strata pozycji rynkowej'
            },
            HIGH_POTENTIAL: {
                title: 'HIGH POTENTIAL - Gotowi na Skalowanie!',
                description: 'Macie solidne fundamenty i jesteście gotowi na transformację. AI może was wynieść na nowy poziom.',
                solutions: [
                    { name: 'Inteligentny ChatBot', saving: '8,000 PLN/mies', icon: <Users className="w-5 h-5" /> },
                    { name: 'Workflow Automation', result: '60% szybciej', icon: <Clock className="w-5 h-5" /> },
                    { name: 'Smart Analytics', result: 'Decyzje w czasie rzeczywistym', icon: <BarChart3 className="w-5 h-5" /> }
                ],
                urgency: 'Optymalne okno na wdrożenie: najbliższe 30 dni'
            },
            READY_TO_SCALE: {
                title: 'READY TO SCALE - Czas na Automatyzację!',
                description: 'Wasze procesy są gotowe na automatyzację. Zacznijcie od quick wins!',
                solutions: [
                    { name: 'AI Assistant', saving: '4,000 PLN/mies', icon: <Sparkles className="w-5 h-5" /> },
                    { name: 'Email Automation', result: '10x engagement', icon: <DollarSign className="w-5 h-5" /> },
                    { name: 'Process Mining', result: 'Odkryj bottlenecki', icon: <AlertCircle className="w-5 h-5" /> }
                ],
                urgency: 'Start w Q1 2025 = przewaga konkurencyjna'
            },
            GROWTH_OPPORTUNITY: {
                title: 'GROWTH OPPORTUNITY - Buduj Przewagę!',
                description: 'Idealny moment na rozpoczęcie przygody z AI. Zacznij od małych kroków.',
                solutions: [
                    { name: 'Starter Bot', saving: '2,000 PLN/mies', icon: <CheckCircle className="w-5 h-5" /> },
                    { name: 'Basic Analytics', result: 'Lepsze decyzje', icon: <TrendingUp className="w-5 h-5" /> }
                ],
                urgency: 'Edukacja teraz = implementacja za 3 miesiące'
            },
            EXPLORATION_PHASE: {
                title: 'EXPLORATION PHASE - Odkryj Możliwości!',
                description: 'Świetnie, że badasz temat AI! Mamy dla Ciebie wartościowe materiały edukacyjne.',
                solutions: [
                    { name: 'Darmowy Pilot', result: '14 dni testów', icon: <Award className="w-5 h-5" /> },
                    { name: 'AI Readiness Audit', result: 'Bezpłatna analiza', icon: <CheckCircle className="w-5 h-5" /> }
                ],
                urgency: 'Wiedza = fundament sukcesu'
            }
        };
        return recommendations[segment];
    };

    const handleAnswer = (value: string | number) => {
        const question = questions[currentQuestion];
        if (question.type === 'multiple') {
            const currentAnswers = answers[question.id] || [];
            const newAnswers = currentAnswers.includes(value)
                ? currentAnswers.filter((v: string) => v !== value)
                : [...currentAnswers, value];
            setAnswers({ ...answers, [question.id]: newAnswers });
        } else {
            setAnswers({ ...answers, [question.id]: value });
            if (question.id === 'industry') setIndustry(value as string);
            else if (question.id === 'team_size') setTeamSize(value as number);
        }
    };

    const nextQuestion = () => {
        if (currentQuestion < questions.length - 1) {
            setCurrentQuestion(currentQuestion + 1);
        } else {
            const results = calculateResults();
            setUserSegment(results);
            setShowResults(true);
        }
    };

    const prevQuestion = () => {
        if (currentQuestion > 0) setCurrentQuestion(currentQuestion - 1);
    };

    const getCurrentQuestion = () => {
        const q = questions[currentQuestion];
        if (q.isDynamic && industry) {
            return { ...q, question: q.question.replace('${painPoint}', industryMapping[industry]?.painPoint || 'powtarzalne zadania') };
        }
        return q;
    };

    const canProceed = () => {
        const question = questions[currentQuestion];
        if (question.type === 'multiple') return answers[question.id]?.length > 0;
        return answers[question.id] !== undefined;
    };

    const progress = ((currentQuestion + 1) / questions.length) * 100;

    // Results View
    if (showResults && userSegment) {
        const recommendations = getRecommendations(userSegment.segment);
        return (
            <>
                <section className="relative min-h-screen overflow-hidden py-12">
                    <ParticlesBackground variant="minimal" />
                    <AnimatedBlob variant="mixed" />
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-bkg-light/50 to-bkg-light dark:via-bkg-dark/50 dark:to-bkg-dark pointer-events-none" />

                    <div id="print-section" className="container mx-auto px-4 max-w-4xl relative z-10">
                        <RevealOnScroll>
                            <GlassCard variant="strong">
                                {/* Score Circle */}
                                <div className="text-center mb-8">
                                    <div className="relative inline-block">
                                        <div className="w-48 h-48 rounded-full bg-gradient-to-br from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark p-1">
                                            <div className="w-full h-full rounded-full bg-bkg-light dark:bg-bkg-dark flex items-center justify-center">
                                                <div>
                                                    <div className="text-6xl font-bold text-text-primary-light dark:text-text-primary-dark">{userSegment.score}%</div>
                                                    <div className="text-sm text-primary-light dark:text-primary-dark mt-2">AI Readiness Score</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="absolute -top-2 -right-2 no-print">
                                            <span className="relative flex h-8 w-8">
                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-light dark:bg-primary-dark opacity-75"></span>
                                                <span className="relative inline-flex rounded-full h-8 w-8 bg-primary-light dark:bg-primary-dark"></span>
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Recommendation Header */}
                                <div className="text-center mb-8 p-6 rounded-2xl bg-gradient-to-r from-primary-light/20 to-secondary-light/20 dark:from-primary-dark/20 dark:to-secondary-dark/20">
                                    <h2 className="text-2xl md:text-3xl font-bold text-text-primary-light dark:text-text-primary-dark mb-4">{recommendations.title}</h2>
                                    <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark">{recommendations.description}</p>
                                    <div className="mt-4 inline-block px-4 py-2 bg-primary-light/20 dark:bg-primary-dark/20 border border-primary-light/50 dark:border-primary-dark/50 rounded-full">
                                        <span className="text-primary-light dark:text-primary-dark font-semibold flex items-center gap-2"><Zap className="w-4 h-4" /> {recommendations.urgency}</span>
                                    </div>
                                </div>

                                {/* Metrics Grid */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                                    <StatCard icon={<DollarSign className="w-6 h-6" />} value={`${userSegment.metrics.monthlySavings.toLocaleString()} PLN`} label="Miesięczne oszczędności" variant="highlight" />
                                    <StatCard icon={<Clock className="w-6 h-6" />} value={`${userSegment.metrics.roiDays} dni`} label="Zwrot inwestycji" />
                                    <StatCard icon={<Zap className="w-6 h-6" />} value={String(userSegment.metrics.automationProcesses)} label="Procesów do automatyzacji" />
                                    <StatCard icon={<TrendingUp className="w-6 h-6" />} value={`${userSegment.metrics.competitiveAdvantage}%`} label="Przewaga konkurencyjna" />
                                </div>

                                {/* Solutions */}
                                <div className="mb-8">
                                    <h3 className="text-xl font-bold text-text-primary-light dark:text-text-primary-dark mb-4">Rekomendowane Rozwiązania:</h3>
                                    <div className="space-y-3">
                                        {recommendations.solutions.map((solution: any, index: number) => (
                                            <GlassCard key={index} variant="subtle" hover className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark flex items-center justify-center text-white">
                                                        {solution.icon}
                                                    </div>
                                                    <div>
                                                        <div className="text-text-primary-light dark:text-text-primary-dark font-semibold">{solution.name}</div>
                                                        <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark">{solution.saving || solution.result}</div>
                                                    </div>
                                                </div>
                                                <ArrowRight className="w-5 h-5 text-primary-light dark:text-primary-dark" />
                                            </GlassCard>
                                        ))}
                                    </div>
                                </div>

                                {/* Export */}
                                <div className="text-center no-print mb-8">
                                    <button onClick={() => window.print()} className="px-6 py-3 bg-white/10 dark:bg-white/5 border border-white/20 dark:border-white/10 text-text-primary-light dark:text-text-primary-dark rounded-xl hover:bg-white/20 dark:hover:bg-white/10 transition-colors inline-flex items-center gap-2">
                                        <Download className="w-5 h-5" /> Pobierz raport PDF
                                    </button>
                                </div>

                                {/* CTA */}
                                <div className="pt-8 border-t border-white/10 text-center no-print">
                                    <h3 className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark mb-4">Zrób kolejny krok</h3>
                                    <p className="text-text-secondary-light dark:text-text-secondary-dark mb-6 max-w-xl mx-auto">Twój raport to pierwszy krok. Teraz przekujmy dane w realne oszczędności i wzrost dla Twojej firmy.</p>
                                    <div className="flex flex-col sm:flex-row justify-center gap-4">
                                        <Link to="/lead-magnet/15-pomyslow-ai" className="px-6 py-3 font-semibold text-primary-light dark:text-primary-dark bg-primary-light/10 dark:bg-primary-dark/10 rounded-xl hover:bg-primary-light/20 dark:hover:bg-primary-dark/20 transition-colors inline-flex items-center justify-center gap-2">
                                            <Download className="w-5 h-5" /> Pobierz PDF: 15 Pomysłów na AI
                                        </Link>
                                        <LeadFormModal
                                            productSlug="ai-quiz"
                                            triggerLabel="Umów bezpłatny Fit Call"
                                            triggerClassName="px-6 py-3 font-semibold text-white bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark rounded-xl hover:shadow-xl hover:shadow-primary-light/30 dark:hover:shadow-primary-dark/30 hover:scale-[1.02] transition-all inline-flex items-center justify-center group"
                                        />
                                    </div>
                                </div>

                                {/* Category Scores */}
                                <div className="mt-8 pt-8 border-t border-white/10">
                                    <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark mb-4">Analiza Szczegółowa:</h3>
                                    <div className="space-y-3">
                                        {Object.entries(userSegment.categoryScores).map(([category, score]) => (
                                            <div key={category} className="flex items-center justify-between">
                                                <span className="text-text-secondary-light dark:text-text-secondary-dark">{questionCategories[category as keyof typeof questionCategories]?.label}</span>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-32 h-2 bg-white/10 dark:bg-white/5 rounded-full overflow-hidden">
                                                        <div className="h-full bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark transition-all duration-1000" style={{ width: `${Math.min(100, score as number)}%` }} />
                                                    </div>
                                                    <span className="text-text-primary-light dark:text-text-primary-dark text-sm font-medium w-12 text-right">{Math.round(score as number)}%</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </GlassCard>
                        </RevealOnScroll>
                    </div>
                </section>
            </>
        );
    }

    // Quiz View
    const currentQ = getCurrentQuestion();

    return (
        <section className="relative min-h-screen overflow-hidden py-12">
            <ParticlesBackground variant="minimal" />
            <AnimatedBlob variant="purple" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-bkg-light/50 to-bkg-light dark:via-bkg-dark/50 dark:to-bkg-dark pointer-events-none" />

            <div className="container mx-auto px-4 max-w-2xl relative z-10">
                <RevealOnScroll>
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark flex items-center justify-center">
                            <Brain className="w-8 h-8 text-white" />
                        </div>
                        <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark bg-clip-text text-transparent bg-[length:200%_200%] animate-gradient">
                            Quiz Transformacji AI
                        </h1>
                        <p className="text-text-secondary-light dark:text-text-secondary-dark mt-2">Odkryj potencjał automatyzacji w 3 minuty</p>
                    </div>
                </RevealOnScroll>

                {/* Progress Bar */}
                <div className="mb-8">
                    <div className="flex justify-between text-sm text-text-secondary-light dark:text-text-secondary-dark mb-2">
                        <span>Pytanie {currentQuestion + 1} z {questions.length}</span>
                        <span>{Math.round(progress)}% ukończone</span>
                    </div>
                    <div className="w-full h-3 bg-white/10 dark:bg-white/5 rounded-full overflow-hidden backdrop-blur">
                        <div className="h-full bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark transition-all duration-500 ease-out" style={{ width: `${progress}%` }} />
                    </div>
                </div>

                <GlassCard variant="strong">
                    <div className="inline-block px-3 py-1 bg-primary-light/20 dark:bg-primary-dark/20 border border-primary-light/50 dark:border-primary-dark/50 rounded-full text-primary-light dark:text-primary-dark text-sm mb-4">
                        {questionCategories[currentQ.category as keyof typeof questionCategories]?.label}
                    </div>

                    <h2 className="text-xl md:text-2xl font-bold text-text-primary-light dark:text-text-primary-dark mb-6">
                        {currentQ.question}
                    </h2>

                    <div className="space-y-3">
                        {currentQ.options.map((option, index) => {
                            const isSelected = currentQ.type === 'multiple'
                                ? answers[currentQ.id]?.includes(option.value)
                                : answers[currentQ.id] === option.value;

                            return (
                                <button
                                    key={index}
                                    onClick={() => handleAnswer(option.value)}
                                    onMouseEnter={() => setHoveredOption(index)}
                                    onMouseLeave={() => setHoveredOption(null)}
                                    className={`w-full p-4 rounded-xl border-2 transition-all duration-300 text-left ${
                                        isSelected
                                            ? 'bg-primary-light/20 dark:bg-primary-dark/20 border-primary-light dark:border-primary-dark scale-[1.02]'
                                            : 'bg-white/5 dark:bg-white/5 border-white/20 dark:border-white/10 hover:bg-white/10 dark:hover:bg-white/10 hover:border-primary-light/50 dark:hover:border-primary-dark/50'
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className={`text-lg ${isSelected ? 'text-text-primary-light dark:text-text-primary-dark font-semibold' : 'text-text-secondary-light dark:text-text-secondary-dark'}`}>
                                            {option.text}
                                        </span>
                                        {isSelected && (
                                            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark flex items-center justify-center">
                                                <CheckCircle className="w-4 h-4 text-white" />
                                            </div>
                                        )}
                                    </div>
                                    {hoveredOption === index && !isSelected && (
                                        <div className="mt-2 text-sm text-primary-light dark:text-primary-dark">+{option.points} punktów</div>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    <div className="flex justify-between items-center mt-8">
                        <button
                            onClick={prevQuestion}
                            disabled={currentQuestion === 0}
                            className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                                currentQuestion === 0
                                    ? 'bg-white/5 text-text-secondary-light/50 dark:text-text-secondary-dark/50 cursor-not-allowed'
                                    : 'bg-white/10 dark:bg-white/5 text-text-primary-light dark:text-text-primary-dark hover:bg-white/20 dark:hover:bg-white/10'
                            }`}
                        >
                            ← Wstecz
                        </button>

                        <button
                            onClick={nextQuestion}
                            disabled={!canProceed()}
                            className={`px-8 py-3 rounded-xl font-semibold transition-all flex items-center gap-2 ${
                                canProceed()
                                    ? 'bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark text-white hover:scale-105 shadow-lg hover:shadow-primary-light/30 dark:hover:shadow-primary-dark/30'
                                    : 'bg-white/5 text-text-secondary-light/50 dark:text-text-secondary-dark/50 cursor-not-allowed'
                            }`}
                        >
                            {currentQuestion === questions.length - 1 ? 'Zobacz wyniki' : 'Dalej'}
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>

                    {currentQ.type === 'multiple' && (
                        <div className="text-center mt-4 text-sm text-text-secondary-light dark:text-text-secondary-dark">
                            Możesz wybrać kilka opcji
                        </div>
                    )}
                </GlassCard>

                {/* Trust Badges */}
                <div className="mt-8 flex justify-center items-center gap-6 text-text-secondary-light dark:text-text-secondary-dark">
                    <div className="flex items-center gap-2">
                        <Shield className="w-5 h-5" />
                        <span className="text-sm">100% Bezpieczne</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Clock className="w-5 h-5" />
                        <span className="text-sm">3 minuty</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Users className="w-5 h-5" />
                        <span className="text-sm">2,847 firm przeszło quiz</span>
                    </div>
                </div>
            </div>
        </section>
    );
};
